import { useState, useEffect } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import GitHubIcon from '@mui/icons-material/GitHub';
import { getAuthProviders } from '../providers/authProvider';

interface Providers {
  google: boolean;
  github: boolean;
}

export const LoginPage = () => {
  const [providers, setProviders] = useState<Providers | null>(null);
  const [loading, setLoading] = useState(true);
  const login = useLogin();
  const notify = useNotify();

  useEffect(() => {
    // Check URL for auth errors
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      notify(`Authentication failed: ${error.replace(/_/g, ' ')}`, { type: 'error' });
    }

    // Load available providers
    getAuthProviders()
      .then(setProviders)
      .finally(() => setLoading(false));
  }, [notify]);

  const handleLogin = (provider: string) => {
    login({ provider });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress />
      </Box>
    );
  }

  const hasProviders = providers?.google || providers?.github;

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
    >
      <Card sx={{ minWidth: 350, maxWidth: 400 }}>
        <CardContent>
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" component="h1" gutterBottom>
              Garuda Admin
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Sign in to access the admin dashboard
            </Typography>
          </Box>

          {!hasProviders ? (
            <Box textAlign="center" py={2}>
              <Typography color="error">
                No authentication providers configured.
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                Please configure Google or GitHub OAuth credentials.
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" flexDirection="column" gap={2}>
                {providers?.google && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<GoogleIcon />}
                    onClick={() => handleLogin('google')}
                    sx={{
                      backgroundColor: '#4285f4',
                      '&:hover': {
                        backgroundColor: '#357ae8',
                      },
                    }}
                  >
                    Continue with Google
                  </Button>
                )}

                {providers?.google && providers?.github && (
                  <Divider>
                    <Typography variant="body2" color="textSecondary">
                      or
                    </Typography>
                  </Divider>
                )}

                {providers?.github && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<GitHubIcon />}
                    onClick={() => handleLogin('github')}
                    sx={{
                      backgroundColor: '#24292e',
                      '&:hover': {
                        backgroundColor: '#1b1f23',
                      },
                    }}
                  >
                    Continue with GitHub
                  </Button>
                )}
              </Box>

              <Box mt={3} textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  By signing in, you agree to the platform's terms of service.
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
